import { endOfDay, startOfDay } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import routing from "routing";
import type { DeckType } from "src/api/flashcardDecks";
import { KnowDontKnowDeckStatisticsModal } from "src/components/Modals/StatisticsModals/KnowDontKnowDeckStatisticsModal";
import FlashcardsSkeleton from "src/components/NewComponents/Skeletons/Flashcards";
import {
	useGetDeckStats,
	useGetFlashcardsCountToResolve,
	useGetUserFlashcardForDeck,
	useResolveFlashcard,
} from "src/hooks/useFlashcards";

import { DefaultApp } from "../DefaultApp";
import { Flashcard, type FlashcardShortcut } from "../Flashcard";
import { KnowDontKnowController } from "../KnowDontKnowController";

interface Deck {
	id: number;
	name: string;
	type: DeckType;
	flashcards: { id: number }[];
}

interface KnowDontKnowAppProps {
	deck: Deck;
	deckCode: string;
}

export function KnowDontKnowApp({ deck, deckCode }: KnowDontKnowAppProps) {
	const { t } = useTranslation();
	const history = useHistory();
	const [resolveIteration, setResolveIteration] = useState(0);
	const [isResolving, setIsResolving] = useState(false);

	const { mutateAsync } = useResolveFlashcard(deckCode);
	const {
		isLoading: isCountLoading,
		data: count,
		refetch: refetchCount,
	} = useGetFlashcardsCountToResolve(deckCode);
	const {
		isLoading: isFlashcardLoading,
		data: flashcardInfo,
		refetch,
	} = useGetUserFlashcardForDeck(deckCode);

	const isAllResolved = !isFlashcardLoading && !flashcardInfo;

	const todayRange = useMemo(() => {
		const now = new Date();
		return {
			startDate: startOfDay(now).toISOString(),
			endDate: endOfDay(now).toISOString(),
		};
	}, []);

	const { data: deckStats, isLoading: isStatsLoading } = useGetDeckStats(
		isAllResolved ? deck.id : null,
		{ ...todayRange, type: deck.type },
	);

	if (isCountLoading || isFlashcardLoading) {
		return <FlashcardsSkeleton className="" />;
	}

	const resolveFlashcard = async (grade: number) => {
		setIsResolving(true);
		try {
			await mutateAsync({
				flashcardRelatedId: flashcardInfo.flashcard.id,
				deckId: deck.id,
				grade,
			});
			await Promise.all([refetch(), refetchCount()]);
			setResolveIteration((c) => c + 1);
		} finally {
			setIsResolving(false);
		}
	};

	const closeAndExit = () => {
		history.push(routing.paths.userDecks);
	};

	const buildShortcuts = (): FlashcardShortcut[] => {
		if (!flashcardInfo) return [];
		return [
			{
				key: "ArrowLeft",
				direction: "left",
				color: "red",
				label: t("decks.knowDontKnow.dontKnow", "Nie znam"),
				action: () => resolveFlashcard(0),
			},
			{
				key: "ArrowRight",
				direction: "right",
				color: "green",
				label: t("decks.knowDontKnow.know", "Znam"),
				action: () => resolveFlashcard(1),
			},
		];
	};

	const renderFlashcard = () => {
		if (!flashcardInfo) return null;
		return (
			<Flashcard
				key={`${flashcardInfo.flashcard.id}-${resolveIteration}`}
				flashcard={flashcardInfo.flashcard.flashcard}
				isLoadingNext={isResolving}
				renderController={(animateExit) => (
					<KnowDontKnowController
						animateExit={animateExit}
						resolveFlashcard={resolveFlashcard}
					/>
				)}
				shortcuts={buildShortcuts()}
			/>
		);
	};

	return (
		<>
			<DefaultApp
				deck={deck}
				deckId={deck.id}
				progress={{
					current: Math.min(
						deck.flashcards.length - count + 1,
						deck.flashcards.length,
					),
					total: deck.flashcards.length,
				}}
				currentMode={deck.type}
				renderFlashcard={renderFlashcard}
			/>
			{isAllResolved && deckStats && (
				<KnowDontKnowDeckStatisticsModal
					onClose={closeAndExit}
					isLoading={isStatsLoading}
					stats={deckStats}
					startDate={todayRange.startDate}
					endDate={todayRange.endDate}
				/>
			)}
		</>
	);
}
